/**
 * CorporateOhsm.js — Investment thesis for GoHimalayan × OHSM.
 * Aligned with the simple bundled SaaS on /host-offer.
 * Gated by admin password.
 *
 * What's NOT in this document (intentionally):
 *   - Cheque size / valuation / equity %
 *   - Deal structure numbers
 *   These stay in the founder's head until diligence is complete.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Lock, Building2, Users, Sparkles,
  ShieldCheck, AlertTriangle, Target, ExternalLink, Landmark,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const GH_BLUE = '#2C5F8D';

/* ─────────────────────────────────────────────────────────────
 * The single bundled SaaS product (same as /host-offer)
 * ────────────────────────────────────────────────────────── */
const BUNDLE_INGREDIENTS = [
  { title: 'Your own page on GoHimalayan',
    body: 'A dedicated editorial page for the property — photos, story, location, WhatsApp CTA. Every guest enquiry lands straight on the host’s phone. No middlemen. No commission on any booking — saves 15–25 % that would otherwise go to OTAs.' },
  { title: 'The OHSM Property Management App',
    body: 'Bookings, billing, housekeeping, incidents, staff attendance, expenses — the entire back-office in one app on the host’s phone. Replaces the paper register and Excel.' },
  { title: 'Featured on GoHimalayan’s Instagram & Facebook',
    body: 'The property is included in our editorial calendar — reels, posts and stories on GoHimalayan’s IG & FB pages, seen by our community of high-intent Himalayan travellers. We do NOT manage the host’s own handles.' },
];

/* One flat price. Same for any property upto 10 rooms. */
const BUNDLE_PRICE = {
  price: '₹11,999',
  unit: '/ year, all-inclusive',
  scope: 'Same price for any property upto 10 rooms',
  terms: 'Annual pre-paid. No monthly billing. No revenue share. No booking commission.',
};

/* ─────────────────────────────────────────────────────────────
 * Government pathways (kept as scenarios, not commitments).
 * Recalibrated to the new ₹11,999 anchor.
 * ────────────────────────────────────────────────────────── */
const GOV_PATHS = [
  {
    label: 'State-Sponsored Digitisation',
    scenario: 'Tourism department pays the full annual fee for every licensed stay.',
    rate: '₹6,000–₹8,000 / property / year',
    who: 'Buyer: State Tourism Department, out of Homestay Development Fund',
    where: 'Best fit: Meghalaya, Sikkim — states with existing digitisation budgets',
    what: 'Zero cost to the host. Every registered stay gets the bundle at signup. Tourism board owns the aggregated data dashboard. State-negotiated rate is roughly half the retail bundle.',
  },
  {
    label: 'Co-funded Subsidy',
    scenario: 'State subsidises 50 %. Host pays half.',
    rate: '₹6,000 host + ₹6,000 state = full bundle',
    who: 'Buyer: State + Host, 50 / 50',
    where: 'Best fit: Himachal Pradesh, Uttarakhand — mature homestay policy, larger volumes',
    what: 'Host has skin in the game (better usage, lower churn). State achieves scale without full-cost commitment. Cleanest to scale.',
  },
  {
    label: 'Voucher / Reimbursement',
    scenario: 'Host buys the bundle at retail; state reimburses on proof of active usage after 6 months.',
    rate: 'FULL RETAIL ₹11,999',
    who: 'Host outlay first, state reimburses 50–100 %',
    where: 'Best fit: Newer states with capex-linked schemes (e.g. Arunachal, Nagaland)',
    what: 'Self-selects for serious hosts. Lowest fiscal risk to the state. Slowest to scale but cleanest politically.',
  },
];

const GOV_SCALE = [
  { state: 'Himachal Pradesh', stays: '3,790+', angle: 'Existing homestay development scheme + state tourism dept. actively digitising' },
  { state: 'Uttarakhand',       stays: '3,550+', angle: 'Recently launched homestay policy 2018; incentives for verified homestays' },
  { state: 'Meghalaya',         stays: '5,118',  angle: 'Meghalaya Tourism Policy 2023 explicitly targets homestay digitisation' },
  { state: 'Sikkim',            stays: '~800',   angle: 'Small, well-governed, ideal pilot state' },
  { state: 'Arunachal / Nagaland', stays: '~1,000 each', angle: 'Nascent policies; opportunity for lighthouse partnership' },
];

/* ─────────────────────────────────────────────────────────────
 * Investment rationale — no financial commitments in this section
 * ────────────────────────────────────────────────────────── */
const INVEST_RATIONALE = [
  {
    icon: Building2,
    title: 'The missing back-end',
    body: 'GoHimalayan gives hosts a beautiful front door. But behind every front door is a property still run on WhatsApp groups, paper registers, and Excel. OHSM is the missing operational spine. Owning both layers means we sell one product, not two, and we own the host relationship end-to-end.',
  },
  {
    icon: Users,
    title: 'Same customer, same acquisition cost',
    body: 'OHSM sells to homestays, B&Bs, small chains. So do we. One outreach conversation, one activation call, one annual bill — doubles the ARPU per host without doubling the acquisition cost. This is a rare 2× lift on unit economics.',
  },
  {
    icon: Sparkles,
    title: 'Direct booking saves the host from OTA commissions',
    body: 'Every booking that lands via GoHimalayan on the host’s WhatsApp is a booking that would otherwise carry a 15–25 % OTA commission. A single ₹10L season on direct bookings clears the ₹11,999 bundle fee in the first fortnight. This is the most tangible ROI a homestay owner has ever been offered.',
  },
  {
    icon: Landmark,
    title: 'The government channel',
    body: 'Every Himalayan state has a homestay digitisation line item. Most have delivered nothing beyond a directory site. A GH+OHSM combined offer — page + PMS + IG/FB features — is a turnkey answer for tourism departments. Small pilot per state can scale to a full-state MoU.',
  },
  {
    icon: ShieldCheck,
    title: 'Small enough to shape, real enough to trust',
    body: 'OHSM is Delhi-NCR based, Startup India recognised, apps launching on iOS and Android, ~28 live properties. Small enough that our capital and channel can multiply their trajectory. Big enough that the product is real — not a slide deck.',
  },
  {
    icon: Target,
    title: 'Defensible against the OTAs',
    body: 'Airbnb and MMT will never build a PMS for a 4-room stay in Tirthan. They optimise for volume, not the ₹11,999 tail. GH+OHSM serves exactly the segment the OTAs abandon — and locks the host in at the operational layer, which is stickier than any commission battle.',
  },
];

const DILIGENCE = [
  'OHSM live properties are ~28. Churn, NPS, and daily-active usage beyond that number are unknown — must be validated.',
  'Product overlap: OHSM has a light booking module. Confirm they will NOT go direct-to-consumer and compete with GoHimalayan’s bookings.',
  'Founder alignment: are OHSM founders ready to be a component in a stack, or do they want to be the platform? Ambition must match.',
  'Technical stack review: engineering quality, deployment maturity, data model, security. Their marketing site is on Wix — the actual app must be inspected.',
  'B2G sales cycles: 9–18 months. Cash runway must survive at least one lost pilot.',
  'Founder lock-in and IP: standard vesting, non-compete on Himalayan geography, source-code escrow.',
];

const NEXT_STEPS = [
  'Founder introduction. Coffee, not pitch. Understand what OHSM founders actually want in the next 24 months.',
  'Request access to a live OHSM demo, 3 host references, and NPS numbers.',
  'Draft a joint pilot spec — 10 GoHimalayan editorial partners onboarded on OHSM at zero cost for 6 months. Measure activation, retention, occupancy uplift.',
  'Prepare a state-tourism pitch deck (Himachal Pradesh or Meghalaya first) using the co-funded model.',
  'Legal: term sheet draft after pilot signals. Numbers stay off paper until diligence closes.',
  'Board sign-off before any signed instrument.',
];

/* ─────────────────────────────────────────────────────────────
 * COMPONENTS
 * ────────────────────────────────────────────────────────── */

const Section = ({ label, title, children }) => (
  <section className="py-16 px-6 lg:px-8 border-t border-stone-200">
    <div className="max-w-5xl mx-auto">
      <p className="text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: GH_BLUE }}>{label}</p>
      <h2 className="text-3xl sm:text-4xl mb-8 text-stone-900" style={pf}>{title}</h2>
      {children}
    </div>
  </section>
);

const Gate = ({ onOk }) => {
  const [pw, setPw] = useState(localStorage.getItem('gh_admin_pw') || '');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const check = () => {
    if (!pw) { setErr('Enter password'); return; }
    setLoading(true); setErr('');
    fetch(`${API}/api/admin/collections/list?password=${encodeURIComponent(pw)}`)
      .then((r) => {
        if (r.ok) { localStorage.setItem('gh_admin_pw', pw); onOk(); }
        else setErr('Wrong password');
      })
      .catch(() => setErr('Network error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (pw) check(); }, []);

  return (
    <div className="min-h-screen bg-stone-950 flex items-center justify-center px-4">
      <div className="bg-stone-900 border border-stone-800 rounded-2xl p-8 max-w-sm w-full">
        <div className="flex items-center gap-2 mb-6 text-amber-200/80">
          <Lock className="h-4 w-4" />
          <p className="text-[10px] tracking-[0.3em] uppercase">Corporate · Investment Board</p>
        </div>
        <h1 className="text-2xl text-white mb-2" style={pf}>GoHimalayan × OHSM</h1>
        <p className="text-sm text-stone-400 mb-6">Investment thesis. Founders &amp; board only.</p>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && check()}
               placeholder="Admin password"
               className="w-full px-4 py-3 bg-stone-950 border border-stone-800 rounded-lg text-white mb-3 focus:outline-none focus:border-amber-200/50"
               data-testid="ohsm-gate-password" />
        {err && <p className="text-xs text-red-400 mb-3">{err}</p>}
        <button onClick={check}
                className="w-full bg-amber-200 text-stone-900 py-3 rounded-lg font-medium tracking-wide hover:bg-amber-100"
                data-testid="ohsm-gate-signin">
          {loading ? 'Checking…' : 'Enter'}
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 * PAGE
 * ────────────────────────────────────────────────────────── */

export default function CorporateOhsm() {
  const [authed, setAuthed] = useState(false);
  if (!authed) return <Gate onOk={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Nav */}
      <div className="bg-stone-950 py-4 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/links" className="text-[11px] uppercase tracking-[0.3em] text-amber-200/70 hover:text-amber-200 flex items-center gap-2">
            <ArrowLeft className="h-3 w-3" /> Back to links
          </Link>
          <p className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Founders &amp; board only</p>
        </div>
      </div>

      {/* Hero */}
      <section className="py-24 px-6 lg:px-8 bg-gradient-to-b from-stone-950 to-stone-900 text-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] tracking-[0.4em] uppercase text-amber-200/70 mb-4">Investment Memo · Draft</p>
          <h1 className="text-4xl sm:text-6xl leading-tight mb-6" style={pf}>
            GoHimalayan <span className="italic text-amber-200">×</span> OHSM
          </h1>
          <p className="text-xl text-stone-300 leading-relaxed mb-8" style={pf}>
            One bundled SaaS product for every small stay in the Himalayas.
            A page, a PMS, and a regular feature on our social. Flat <strong className="text-amber-200">₹11,999 / year</strong>.
            Sold to hosts directly, or subsidised through state tourism departments.
          </p>
          <div className="flex flex-wrap gap-6 text-sm text-stone-400">
            <span>Prepared for: Board</span>
            <span>Status: Draft — pre-diligence</span>
            <span>Circulation: Confidential</span>
          </div>
        </div>
      </section>

      {/* Executive summary */}
      <Section label="Executive summary" title="The thesis in one page">
        <div className="prose max-w-none text-stone-700 leading-relaxed space-y-4 text-lg">
          <p>
            GoHimalayan has proven the editorial and discovery layer. What we
            haven’t built — and don’t want to build — is the
            operational spine: bookings, billing, housekeeping, staff, expenses.
            Every host we onboard still runs the property on WhatsApp groups and
            paper registers.
          </p>
          <p>
            <strong>OHSM</strong> is a Delhi-NCR based, Startup India recognised
            property-management app for stays with 0–20 rooms. Their software
            handles the entire back-office. Their apps are launching on iOS and Android.
            Priced at ₹999 per room per year, standalone.
          </p>
          <p>
            The combination is a single bundled SaaS product — the host&rsquo;s own page
            on GoHimalayan, the OHSM app, and a regular feature on GoHimalayan&rsquo;s
            Instagram & Facebook pages. Sold at a flat <strong>₹11,999 per property
            per year</strong>, for any property upto 10 rooms. Simple. Predictable ARR.
            No commissions, no revenue share.
          </p>
          <p className="text-base text-stone-600 italic">
            At scale, the same bundle plugs into state tourism budgets as a subsidised
            offering — giving every licensed homestay in the Himalayas a page, an
            app, and social visibility, without asking each host to figure it out.
          </p>
        </div>
      </Section>

      {/* The product — what we're selling */}
      <Section label="The product" title="One bundled SaaS — three ingredients">
        <p className="text-stone-600 mb-8 max-w-3xl">
          Same product for every host. Same price whether it is a 3-room family
          homestay or a 10-room B&B. Everything is annual, pre-paid, no commissions.
        </p>
        <div className="grid md:grid-cols-3 gap-5">
          {BUNDLE_INGREDIENTS.map((it) => (
            <div key={it.title} className="bg-white rounded-2xl border border-stone-200 p-6">
              <p className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: GH_BLUE }}>Included</p>
              <h3 className="text-lg mb-2" style={pf}>{it.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>

        {/* One flat price — hero block matching /host-offer */}
        <div className="rounded-2xl p-8 mt-10 flex flex-col md:flex-row items-center justify-between gap-6"
             style={{ backgroundColor: GH_BLUE, color: 'white' }}
             data-testid="bundle-price">
          <div className="flex-1">
            <p className="text-[11px] tracking-[0.35em] uppercase text-amber-200 mb-3">One flat price</p>
            <div className="flex items-baseline gap-4">
              <span className="text-7xl font-light leading-none" style={pf}>{BUNDLE_PRICE.price}</span>
              <span className="text-base text-stone-200">/ year</span>
            </div>
            <p className="text-base text-stone-100 mt-5">{BUNDLE_PRICE.scope}. {BUNDLE_PRICE.terms}</p>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className="text-[11px] tracking-[0.25em] uppercase text-amber-200 mb-2">Includes</p>
            <ul className="text-sm text-stone-100 space-y-1.5">
              <li>✓ GoHimalayan page + WhatsApp direct booking</li>
              <li>✓ OHSM Property Management App</li>
              <li>✓ Regular feature on our IG &amp; FB</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Government pathways */}
      <Section label="The scale unlock" title="How this works through state tourism">
        <p className="text-stone-600 mb-8 max-w-3xl">
          Every Himalayan state has a homestay digitisation budget line. Most have
          delivered nothing beyond a directory website. Three ways a state
          department can adopt the bundle:
        </p>
        <div className="space-y-4">
          {GOV_PATHS.map((g) => (
            <div key={g.label} className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex flex-col md:flex-row md:items-start md:gap-6">
                <div className="md:w-1/3 mb-4 md:mb-0">
                  <p className="text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: GH_BLUE }}>{g.label}</p>
                  <p className="text-2xl font-light text-stone-900 leading-tight" style={pf}>{g.rate}</p>
                  <p className="text-xs text-stone-500 mt-2">{g.who}</p>
                </div>
                <div className="md:flex-1">
                  <p className="text-sm text-stone-700 mb-2 font-medium">{g.scenario}</p>
                  <p className="text-sm text-stone-600 leading-relaxed mb-2">{g.what}</p>
                  <p className="text-xs text-stone-500 italic">{g.where}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* State volumes */}
        <h3 className="text-xl mt-12 mb-4" style={pf}>Addressable universe · By state</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {GOV_SCALE.map((s) => (
            <div key={s.state} className="bg-stone-100 rounded-xl p-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase text-stone-500 mb-1">{s.state}</p>
                <p className="text-2xl font-light" style={pf}>{s.stays}</p>
                <p className="text-[11px] text-stone-500 mt-2 leading-snug">{s.angle}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm text-stone-500 mt-6 italic">
          Even at the co-funded 50 / 50 rate (₹6,000 state + ₹6,000 host = ₹11,999
          per property), one Himachal contract at 3,790 stays yields a floor of
          <strong> ~₹4.5 Cr in annual recurring revenue</strong>. Meghalaya alone
          (5,118 stays) could exceed ₹6 Cr in a single MoU.
        </p>
      </Section>

      {/* Why invest */}
      <Section label="Investment rationale" title="Why owning the back-end matters">
        <div className="grid md:grid-cols-2 gap-6">
          {INVEST_RATIONALE.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.title} className="bg-white rounded-2xl border border-stone-200 p-6">
                <Icon className="h-5 w-5 mb-3" style={{ color: GH_BLUE }} />
                <h3 className="text-lg mb-2" style={pf}>{r.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{r.body}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Diligence */}
      <Section label="Open questions" title="What must be validated before we commit">
        <p className="text-stone-600 mb-6 max-w-3xl">
          Numbers in this document are directional. All financial commitments —
          instrument, size, valuation, structure — remain with the founder until
          diligence closes and the board approves. Everything below must be
          answered first.
        </p>
        <ul className="space-y-3">
          {DILIGENCE.map((r, i) => (
            <li key={i} className="flex gap-3 text-sm text-stone-700 leading-relaxed">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Next steps */}
      <Section label="Next steps" title="Six weeks, one meeting, one pilot">
        <ol className="space-y-3">
          {NEXT_STEPS.map((n, i) => (
            <li key={i} className="flex gap-4 text-sm text-stone-700 leading-relaxed">
              <span className="font-mono" style={{ color: GH_BLUE }}>{String(i + 1).padStart(2, '0')}</span>
              <span>{n}</span>
            </li>
          ))}
        </ol>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <a href="https://www.ohsm.in/" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 text-sm hover:opacity-70"
             style={{ color: GH_BLUE }}>
            <ExternalLink className="h-4 w-4" /> ohsm.in
          </a>
          <a href="/host-offer" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 text-sm hover:opacity-70"
             style={{ color: GH_BLUE }}>
            <ExternalLink className="h-4 w-4" /> The host-facing pitch (/host-offer)
          </a>
        </div>
      </Section>

      {/* Footer */}
      <div className="bg-stone-950 text-stone-500 py-8 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-[10px] tracking-[0.3em] uppercase">
          <span>Confidential · Founders &amp; board only</span>
          <Link to="/links" className="hover:text-amber-200">← Links</Link>
        </div>
      </div>
    </div>
  );
}

/* Trigger imports we still use */
